import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase.js'
import { apiFetch } from '../../lib/api.js'
import Modal from '../ui/Modal.jsx'
import { VisualCard, AddCard } from '../ui/VisualCard.jsx'

const BUCKET = 'theme-backgrounds'

// Compresse et convertit en WebP (max 1920×1080, qualité 82%).
// source peut être un File/Blob (upload) ou une URL string (optimisation).
async function compressToWebP(source) {
  const isBlob = source instanceof File || source instanceof Blob
  const url = isBlob ? URL.createObjectURL(source) : source

  return new Promise((resolve, reject) => {
    const img = new Image()
    if (!isBlob) img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (isBlob) URL.revokeObjectURL(url)
      const MAX_W = 1920, MAX_H = 1080
      let w = img.naturalWidth, h = img.naturalHeight
      if (w > MAX_W || h > MAX_H) {
        const ratio = Math.min(MAX_W / w, MAX_H / h)
        w = Math.round(w * ratio)
        h = Math.round(h * ratio)
      }
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Compression échouée')),
        'image/webp',
        0.82
      )
    }
    img.onerror = () => reject(new Error("Impossible de charger l'image"))
    img.src = url
  })
}

function isAlreadyOptimized(imageUrl) {
  if (!imageUrl) return false
  try { return new URL(imageUrl).pathname.endsWith('.webp') } catch { return false }
}

// ── Upload image vers Supabase Storage ───────────────────────
async function uploadImage(blob) {
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.webp`
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { upsert: false, contentType: 'image/webp' })
  if (error) throw new Error(error.message)
  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return { publicUrl, path }
}

async function deleteImage(imageUrl) {
  if (!imageUrl) return
  const path = imageUrl.split(`/${BUCKET}/`)[1]
  if (path) await supabase.storage.from(BUCKET).remove([path])
}


// ── Génération du prompt IA ───────────────────────────────────
function buildPrompt(label) {
  const sujet = label.trim() || 'thème de circuit'
  return `Aerial isometric view at 45 degrees, stylized game background art inspired by fun arcade racing games, fully transposed into a ${sujet} universe. A visible sky and distant horizon line appear at the very top of the image, creating depth and scale. The composition is strongly centered: the main thematic elements, landmarks, structures, and visual focus points are concentrated inside a compact oval-shaped central area covering roughly 50% of the image width and height. All central elements — buildings, structures, landmarks — must remain low-profile so that nothing extends or protrudes visually beyond the oval boundary; avoid any tall towers, spires, or high-rise buildings that would break out of the central zone. Use fewer, larger, and more readable elements rather than many small details. Prioritize strong silhouettes, clear shapes, and simplified composition for good gameplay readability. The surrounding areas remain lighter and more open, with sparse secondary decorations and environmental details placed mainly near the four corners, preserving visual balance and negative space. No roads, race tracks, or paths visible. Vibrant and saturated colors, immersive atmosphere, medium detail level, clean shapes, sharp lines, cohesive worldbuilding, 1920x1080 landscape format.`
}

// ── Formulaire création / édition ─────────────────────────────
function ThemeForm({ initial, onSubmit, onCancel, loading }) {
  const [label, setLabel] = useState(initial?.label ?? '')
  const [imageFile, setImageFile] = useState(null)
  const [preview, setPreview] = useState(initial?.image_url ?? null)
  const [copied, setCopied] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const fileRef = useRef()

  const prompt = buildPrompt(label)

  async function handleCopyPrompt() {
    await navigator.clipboard.writeText(prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setImageFile(file)
    setPreview(URL.createObjectURL(file))
  }

  function handleRemoveImage() {
    setImageFile(null)
    setPreview(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <form
      onSubmit={e => { e.preventDefault(); onSubmit({ label, imageFile, existingImageUrl: initial?.image_url ?? null }) }}
      className="space-y-4"
    >
      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Libellé</label>
        <input
          required
          value={label}
          onChange={e => setLabel(e.target.value)}
          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="ex. Ville"
        />
      </div>

      {/* Prompt IA */}
      <div className="bg-indigo-500/10 border border-indigo-400/30 rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-indigo-300 uppercase tracking-wide">✨ Prompt IA suggéré</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowPrompt(v => !v)}
              className="text-xs px-2 py-1 rounded-lg text-indigo-400/70 hover:text-indigo-300 transition"
            >
              {showPrompt ? 'Masquer' : 'Afficher'}
            </button>
            <button
              type="button"
              onClick={handleCopyPrompt}
              className={`text-xs px-3 py-1 rounded-lg font-medium transition ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-indigo-500/30 text-indigo-300 hover:bg-indigo-500/50'
              }`}
            >
              {copied ? '✓ Copié !' : 'Copier'}
            </button>
          </div>
        </div>
        {showPrompt && (
          <p className="text-xs text-indigo-200 leading-relaxed italic">{prompt}</p>
        )}
      </div>

      {/* Zone image */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-white/80">Image de fond</label>
          <span className="text-xs text-white/30">Convertie automatiquement en WebP ≤ 1920×1080</span>
        </div>
        {preview ? (
          <div className="relative rounded-xl overflow-hidden h-40">
            <img src={preview} alt="Aperçu" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-3 text-white font-semibold">{label}</div>
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-red-600"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            className="w-full h-32 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-2 text-white/40 hover:border-blue-400 hover:text-blue-400 transition"
          >
            <span className="text-3xl">📁</span>
            <span className="text-sm">Cliquer pour charger une image</span>
            <span className="text-xs text-white/30">Convertie automatiquement en WebP ≤ 1920×1080</span>
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
        />
        {preview && !imageFile && (
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            className="mt-2 text-sm text-blue-400 hover:underline"
          >
            Changer l'image
          </button>
        )}
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-white/60 hover:text-white transition">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="btn-primary px-6 py-2 disabled:opacity-60">
          {loading ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}

// ── Composant principal ───────────────────────────────────────
export default function ThemeManager() {
  const [themes, setThemes] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | 'create' | theme objet
  const [saving, setSaving] = useState(false)
  const [optimizing, setOptimizing] = useState(null) // id du thème en cours d'optimisation
  const [error, setError] = useState('')

  async function fetchThemes() {
    setLoading(true)
    try {
      const data = await apiFetch('/api/themes')
      setThemes(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  useEffect(() => { fetchThemes() }, [])

  async function handleSubmit({ label, imageFile, existingImageUrl }) {
    setSaving(true)
    setError('')
    try {
      let image_url = existingImageUrl

      if (imageFile) {
        if (existingImageUrl) await deleteImage(existingImageUrl)
        const compressed = await compressToWebP(imageFile)
        const { publicUrl } = await uploadImage(compressed)
        image_url = publicUrl
      }

      // Image supprimée manuellement (preview=null sans nouveau fichier)
      if (!imageFile && !existingImageUrl) {
        image_url = null
      }

      if (modal === 'create') {
        await apiFetch('/api/themes', {
          method: 'POST',
          body: JSON.stringify({ label, image_url }),
        })
      } else {
        await apiFetch(`/api/themes/${modal.id}`, {
          method: 'PUT',
          body: JSON.stringify({ label, image_url }),
        })
      }
      setModal(null)
      await fetchThemes()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  async function handleOptimize(theme) {
    setOptimizing(theme.id)
    setError('')
    try {
      const compressed = await compressToWebP(theme.image_url)
      await deleteImage(theme.image_url)
      const { publicUrl } = await uploadImage(compressed)
      await apiFetch(`/api/themes/${theme.id}`, {
        method: 'PUT',
        body: JSON.stringify({ image_url: publicUrl }),
      })
      await fetchThemes()
    } catch (e) {
      setError(`Erreur lors de l'optimisation : ${e.message}`)
    }
    setOptimizing(null)
  }

  async function handleDelete(theme) {
    if (!confirm(`Supprimer le thème "${theme.label}" ?`)) return
    setError('')
    try {
      await deleteImage(theme.image_url)
      await apiFetch(`/api/themes/${theme.id}`, { method: 'DELETE' })
      setThemes(ts => ts.filter(t => t.id !== theme.id))
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-white">Thèmes visuels</h1>
        <button onClick={() => setModal('create')} className="btn-primary px-4 py-2">
          + Nouveau thème
        </button>
      </div>

      {error && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-2 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="text-white/40 py-12 text-center">Chargement…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {themes.map(theme => (
            <VisualCard
              key={theme.id}
              imageUrl={theme.image_url}
              headerContent={
                <div className="text-white font-semibold text-base drop-shadow-md">{theme.label}</div>
              }
              actions={
                <>
                  {theme.image_url && !isAlreadyOptimized(theme.image_url) && (
                    <button
                      onClick={() => handleOptimize(theme)}
                      disabled={optimizing === theme.id}
                      className="bg-amber-500/80 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow hover:bg-amber-500 transition disabled:opacity-50"
                    >
                      {optimizing === theme.id ? '⏳ Conversion…' : '⚡ Optimiser'}
                    </button>
                  )}
                  <button
                    onClick={() => setModal(theme)}
                    className="bg-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow hover:bg-white/30 transition"
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(theme)}
                    className="bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow hover:bg-red-600 transition"
                  >
                    🗑 Supprimer
                  </button>
                </>
              }
            />
          ))}
          <AddCard onClick={() => setModal('create')} label="Nouveau thème" />
        </div>
      )}

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'create' ? 'Nouveau thème' : 'Modifier le thème'}
      >
        {modal !== null && (
          <ThemeForm
            initial={modal !== 'create' ? modal : undefined}
            onSubmit={handleSubmit}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        )}
        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      </Modal>
    </div>
  )
}
